<template>
  <div :style="themePageStyle">
    <div class="container" style="padding-top:80px;padding-bottom:100px;max-width:900px;">

      <!-- Header -->
      <div class="d-flex align-items-center mb-3">
        <button class="btn btn-sm btn-outline-secondary me-2" @click="$router.go(-1)"><i class="mdi mdi-arrow-left me-1"></i>Back</button>
        <h5 class="mb-0 fw-bold" :style="{color:darkMode?'#F5F0E8':'#1A1A1A'}"><i class="mdi mdi-account-multiple-plus me-2" style="color:#BFA164;"></i>My Referrals</h5>
      </div>

      <!-- Code card with QR (Alex 2026-07-17 — link swapped for a scan-friendly QR;
           Register.vue already prefills ?ref=CODE, so scanning the QR opens signup
           with the code auto-populated). -->
      <div class="mr-code-card" :class="{dark:darkMode}">
        <div class="mr-code-label">Your Referral Code</div>
        <div class="mr-code">{{ referralCode || '—' }}</div>
        <div class="mr-code-sub">Every farmer who signs up with this code is credited to you.</div>

        <div v-if="shareUrl" class="mr-qr-wrap">
          <img :src="qrImageUrl" alt="Scan to sign up with your referral code" class="mr-qr-img" @click="qrOpen = true" />
          <div class="mr-qr-hint">
            <i class="mdi mdi-qrcode-scan me-1"></i>Tap to enlarge · farmers can scan to sign up
          </div>
        </div>

        <div class="d-flex gap-2 flex-wrap justify-content-center mt-3">
          <button class="btn btn-sm btn-warning" @click="copy(referralCode)" :disabled="!referralCode"><i class="mdi mdi-content-copy me-1"></i>Copy code</button>
          <a v-if="whatsappLink" :href="whatsappLink" target="_blank" class="btn btn-sm btn-success"><i class="mdi mdi-whatsapp me-1"></i>WhatsApp</a>
          <a v-if="smsLink" :href="smsLink" class="btn btn-sm btn-outline-info"><i class="mdi mdi-message-text me-1"></i>SMS</a>
          <button class="btn btn-sm btn-outline-secondary" @click="downloadQR" :disabled="!shareUrl"><i class="mdi mdi-download me-1"></i>Save QR</button>
        </div>
      </div>

      <!-- Full-screen QR modal for showing on a big screen so people can scan easily -->
      <div v-if="qrOpen" class="mr-modal-backdrop" @click.self="qrOpen = false">
        <div class="mr-qr-modal" :class="{dark:darkMode}">
          <button class="mr-modal-close" @click="qrOpen = false"><i class="mdi mdi-close"></i></button>
          <div class="mr-code-label">Scan to sign up with</div>
          <div class="mr-code mr-code-modal">{{ referralCode }}</div>
          <img :src="qrImageUrlLarge" alt="Referral QR code" class="mr-qr-img-large" />
          <div class="mr-code-sub mt-2">Point a phone camera at this code.</div>
          <div v-if="shareUrl" class="mr-share-link mt-2">{{ shareUrl }}</div>
        </div>
      </div>

      <!-- Summary — each tile is a clickable filter. Click again to clear. -->
      <div class="row g-2 mt-3 mb-3">
        <div class="col-6 col-md-3">
          <div class="mr-stat" :class="{dark:darkMode, active: filter==='all'}" @click="setFilter('all')">
            <div class="mr-stat-val">{{ totalReferred }}</div>
            <div class="mr-stat-lbl">Total Brought In</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="mr-stat" :class="{dark:darkMode, active: filter==='active'}" @click="setFilter('active')">
            <div class="mr-stat-val">{{ activeCount }}</div>
            <div class="mr-stat-lbl">Active Last 30d</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="mr-stat" :class="{dark:darkMode, active: filter==='listed'}" @click="setFilter('listed')">
            <div class="mr-stat-val">{{ withListings }}</div>
            <div class="mr-stat-lbl">Have Listed</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="mr-stat" :class="{dark:darkMode, active: filter==='transacted'}" @click="setFilter('transacted')">
            <div class="mr-stat-val">{{ withOrders }}</div>
            <div class="mr-stat-lbl">Have Transacted</div>
          </div>
        </div>
      </div>

      <!-- List -->
      <div class="mr-list-card" :class="{dark:darkMode}">
        <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <h6 class="fw-bold mb-0" :style="{color:darkMode?'#F5F0E8':'#1A1A1A'}">
            <i class="mdi mdi-format-list-bulleted me-1"></i>{{ listHeading }}
            <span style="font-weight:400;color:var(--fm-s,#6B6356);font-size:13px;">({{ filteredReferrals.length }})</span>
          </h6>
          <button v-if="filter !== 'all'" class="btn btn-sm btn-outline-secondary" @click="setFilter('all')">
            <i class="mdi mdi-close me-1"></i>Clear filter
          </button>
        </div>
        <div v-if="loading" class="text-center py-4 text-muted"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>
        <div v-else-if="!referrals.length" class="text-center py-4">
          <i class="mdi mdi-account-multiple-outline" style="font-size:42px;color:#BFA164;opacity:.5;"></i>
          <p class="mt-2 mb-1" :style="{color:darkMode?'#F5F0E8':'#1A1A1A'}">No referrals yet</p>
          <p class="mb-0" style="font-size:13px;color:var(--fm-m,#A09888);">Share your code above with farmers — they enter it on signup and they'll appear here.</p>
        </div>
        <div v-else-if="!filteredReferrals.length" class="text-center py-4">
          <i class="mdi mdi-filter-off-outline" style="font-size:36px;color:#BFA164;opacity:.5;"></i>
          <p class="mt-2 mb-1" :style="{color:darkMode?'#F5F0E8':'#1A1A1A'}">Nobody matches this filter yet</p>
          <p class="mb-0" style="font-size:13px;color:var(--fm-m,#A09888);">Click "Clear filter" above to see everyone.</p>
        </div>
        <div v-else>
          <div v-for="r in filteredReferrals" :key="r.id" class="mr-list-row mr-list-row-clickable" @click="openReferralDetail(r)">
            <div class="mr-avatar">{{ initials(r.display_name) }}</div>
            <div class="flex-fill">
              <div class="fw-bold" :style="{color:darkMode?'#F5F0E8':'#1A1A1A'}">{{ r.display_name }}</div>
              <div style="font-size:11px;color:var(--fm-m,#A09888);">
                <i class="mdi mdi-phone-outline"></i> {{ r.phone }}
                <span v-if="r.county_name"> · <i class="mdi mdi-map-marker-outline"></i> {{ r.county_name }}</span>
                · {{ fDate(r.created_at) }}
              </div>
            </div>
            <div class="text-end" style="font-size:11px;color:var(--fm-m,#A09888);">
              <div v-if="Number(r.listing_count)"><span class="badge bg-warning bg-opacity-15 text-warning">{{ r.listing_count }} listing<span v-if="Number(r.listing_count)>1">s</span></span></div>
              <div v-if="Number(r.order_count)" class="mt-1"><span class="badge bg-success bg-opacity-15 text-success">{{ r.order_count }} order<span v-if="Number(r.order_count)>1">s</span></span></div>
              <div v-if="Number(r.buy_request_count)" class="mt-1"><span class="badge bg-info bg-opacity-15 text-info">{{ r.buy_request_count }} buy request<span v-if="Number(r.buy_request_count)>1">s</span></span></div>
              <div v-if="Number(r.agro_shop_count)" class="mt-1"><span class="badge" style="background:rgba(191,161,100,.15);color:#BFA164;">Agro Shop</span></div>
              <div v-if="!Number(r.listing_count) && !Number(r.order_count) && !Number(r.buy_request_count) && !Number(r.agro_shop_count)" class="text-muted">Joined · no activity yet</div>
            </div>
            <i class="mdi mdi-chevron-right" style="color:var(--fm-s,#6B6356);font-size:22px;"></i>
          </div>
        </div>
      </div>

      <!-- Referral drill-down modal — richer picture per person than the row -->
      <div v-if="detail" class="mr-modal-backdrop" @click.self="detail = null">
        <div class="mr-detail-modal" :class="{dark:darkMode}">
          <button class="mr-modal-close" @click="detail = null"><i class="mdi mdi-close"></i></button>
          <div class="d-flex align-items-center gap-3 mb-3">
            <div class="mr-avatar" style="width:56px;height:56px;font-size:18px;">{{ initials(detail.display_name) }}</div>
            <div>
              <div class="fw-bold" style="font-size:18px;" :style="{color:darkMode?'#F5F0E8':'#1A1A1A'}">{{ detail.display_name }}</div>
              <div style="font-size:12px;color:var(--fm-m,#A09888);">
                <i class="mdi mdi-phone-outline"></i> {{ detail.phone }}
                <span v-if="detail.county_name"> · <i class="mdi mdi-map-marker-outline"></i> {{ detail.county_name }}</span>
              </div>
            </div>
          </div>

          <div class="row g-2 mb-3">
            <div class="col-6"><div class="mr-detail-tile" :class="{dark:darkMode}"><div class="mr-detail-tile-val">{{ Number(detail.listing_count) || 0 }}</div><div class="mr-detail-tile-lbl">Listings</div></div></div>
            <div class="col-6"><div class="mr-detail-tile" :class="{dark:darkMode}"><div class="mr-detail-tile-val">{{ Number(detail.order_count) || 0 }}</div><div class="mr-detail-tile-lbl">Orders</div></div></div>
            <div class="col-6"><div class="mr-detail-tile" :class="{dark:darkMode}"><div class="mr-detail-tile-val">{{ Number(detail.buy_request_count) || 0 }}</div><div class="mr-detail-tile-lbl">Buy Requests</div></div></div>
            <div class="col-6"><div class="mr-detail-tile" :class="{dark:darkMode}"><div class="mr-detail-tile-val">{{ Number(detail.agro_shop_count) || 0 }}</div><div class="mr-detail-tile-lbl">Agro Shops</div></div></div>
          </div>

          <div class="mr-detail-row"><span>Joined</span><strong>{{ fDate(detail.created_at) }}</strong></div>
          <div class="mr-detail-row"><span>Last active</span><strong>{{ detail.last_active_at ? fDate(detail.last_active_at) : '—' }}</strong></div>
          <div class="mr-detail-row"><span>Status</span>
            <strong>
              <span v-if="isActive(detail)" style="color:#50C878;"><i class="mdi mdi-circle" style="font-size:8px;"></i> Active last 30d</span>
              <span v-else style="color:var(--fm-s,#6B6356);"><i class="mdi mdi-circle-outline" style="font-size:8px;"></i> Dormant</span>
            </strong>
          </div>
          <div v-if="hasActivity(detail)" class="mr-detail-row"><span>Contribution</span>
            <strong style="color:#BFA164;">Transacted · counts toward your target</strong>
          </div>
          <div v-else class="mr-detail-row" style="color:#FF9800;">
            <span>Nudge</span>
            <strong>Encourage them to list or place a buy request</strong>
          </div>

          <div class="d-flex gap-2 mt-3 flex-wrap">
            <a :href="'tel:' + detail.phone" class="btn btn-sm btn-outline-warning flex-fill"><i class="mdi mdi-phone me-1"></i>Call</a>
            <a :href="'https://wa.me/' + waPhone(detail.phone) + '?text=' + encodeURIComponent(nudgeText(detail))" target="_blank" class="btn btn-sm btn-success flex-fill"><i class="mdi mdi-whatsapp me-1"></i>WhatsApp</a>
            <a href="javascript:void(0)" @click="$router.push('/u/' + detail.id)" class="btn btn-sm btn-outline-secondary flex-fill"><i class="mdi mdi-account-circle me-1"></i>Profile</a>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, flocker } from '@/api';

export default {
  name: 'MyReferrals',
  data() {
    return {
      darkMode: (document.documentElement.getAttribute('data-theme') === 'dark'),
      loading: true,
      referralCode: '',
      referrals: [],
      shareUrl: '',
      whatsappLink: '',
      smsBody: '',
      qrOpen: false,     // enlarge-QR modal
      detail: null,      // clicked referral drill-down
      filter: 'all'      // 'all' | 'active' | 'listed' | 'transacted'
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    totalReferred(){ return this.referrals.length; },
    activeCount(){
      var thirty = Date.now() - 30*24*60*60*1000;
      return this.referrals.filter(function(r){ return r.last_active_at && new Date(r.last_active_at).getTime() >= thirty; }).length;
    },
    withListings(){ return this.referrals.filter(function(r){ return Number(r.listing_count) > 0; }).length; },
    withOrders(){ return this.referrals.filter(function(r){ return Number(r.order_count) > 0; }).length; },
    withActivity(){ return this.referrals.filter(function(r){ return Number(r.listing_count)>0 || Number(r.order_count)>0 || Number(r.buy_request_count)>0 || Number(r.agro_shop_count)>0; }).length; },
    smsLink(){ return this.smsBody ? ('sms:?body=' + encodeURIComponent(this.smsBody)) : ''; },
    // QR code images generated via api.qrserver.com — free, no auth, no external
    // dep. The URL encoded IS the shareUrl which already carries ?ref=CODE, and
    // Register.vue prefills the code from that query string on load.
    qrImageUrl(){
      if (!this.shareUrl) return '';
      return 'https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=' + encodeURIComponent(this.shareUrl);
    },
    qrImageUrlLarge(){
      if (!this.shareUrl) return '';
      return 'https://api.qrserver.com/v1/create-qr-code/?size=500x500&margin=12&data=' + encodeURIComponent(this.shareUrl);
    },
    // Filter referrals by the tile clicked at the top. Each tile drills into the
    // people counted in that metric.
    filteredReferrals(){
      var self = this;
      if (this.filter === 'active') {
        var thirty = Date.now() - 30*24*60*60*1000;
        return this.referrals.filter(function(r){ return r.last_active_at && new Date(r.last_active_at).getTime() >= thirty; });
      }
      if (this.filter === 'listed') {
        return this.referrals.filter(function(r){ return Number(r.listing_count) > 0; });
      }
      if (this.filter === 'transacted') {
        return this.referrals.filter(function(r){ return Number(r.order_count) > 0; });
      }
      return this.referrals;
    },
    listHeading(){
      switch (this.filter) {
        case 'active':     return 'Active last 30 days';
        case 'listed':     return 'Have posted listings';
        case 'transacted': return 'Have transacted';
        default:           return "Who you've brought in";
      }
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        var f = new FormData();
        f.append('function', 'getMyReferrals');
        f.append('userId', sessionStorage.getItem('userId') || '');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.referralCode = r.data.data.referralCode || '';
          this.referrals    = r.data.data.referrals    || [];
          this.shareUrl     = r.data.data.shareUrl     || '';
          this.whatsappLink = r.data.data.whatsappLink || '';
          this.smsBody      = r.data.data.smsBody      || '';
        }
      } catch (e) { console.error('load referrals', e); }
      this.loading = false;
    },
    initials(name) {
      if (!name) return '?';
      var parts = String(name).trim().split(/\s+/);
      return (parts[0][0] + (parts[1]?parts[1][0]:'')).toUpperCase();
    },
    copy(text) {
      if (!text) return;
      try { navigator.clipboard.writeText(text); }
      catch (e) {
        var ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch(_){}
        document.body.removeChild(ta);
      }
    },
    fDate(d) {
      if (!d) return '';
      var dt = new Date(d);
      return dt.toLocaleDateString('en-KE', { day:'numeric', month:'short', year:'numeric' });
    },
    openReferralDetail(r){ this.detail = r; },
    setFilter(f){
      // Click the same tile again to clear back to "all".
      this.filter = (this.filter === f && f !== 'all') ? 'all' : f;
    },
    hasActivity(r){
      return Number(r.listing_count) > 0 || Number(r.order_count) > 0
          || Number(r.buy_request_count) > 0 || Number(r.agro_shop_count) > 0;
    },
    isActive(r){
      if (!r.last_active_at) return false;
      var t = new Date(r.last_active_at).getTime();
      return (Date.now() - t) < 30 * 24 * 60 * 60 * 1000;
    },
    waPhone(p){
      // Normalise to E.164 without the '+' (wa.me expects that format).
      var n = String(p || '').replace(/\D/g, '');
      if (n.length === 9) n = '254' + n;
      if (n.length === 10 && n.charAt(0) === '0') n = '254' + n.substr(1);
      return n;
    },
    nudgeText(r){
      var first = (r.display_name || '').split(' ')[0] || 'there';
      if (Number(r.listing_count) > 0 || Number(r.order_count) > 0) {
        return "Hi " + first + ", saw you're active on Flockr. Anything I can help with?";
      }
      return "Hi " + first + ", have you tried posting a listing or a buy request on Flockr? Happy to walk you through it.";
    },
    downloadQR(){
      if (!this.shareUrl) return;
      // Use the large size for download; open in a new tab so the browser handles
      // save-as. Direct <a download> won't work cross-origin.
      window.open(this.qrImageUrlLarge, '_blank');
    }
  }
};
</script>

<style scoped>
.mr-code-card { background:#FFFFFF; border:2px solid #BFA164; border-radius:14px; padding:24px 20px; text-align:center; }
.mr-code-card.dark { background:#141414; }
.mr-code-label { font-size:11px; color:#6B6356; text-transform:uppercase; letter-spacing:1px; font-weight:600; }
.mr-code { font-size:42px; font-weight:800; color:#BFA164; letter-spacing:8px; font-family:'Courier New',monospace; margin:6px 0; }
.mr-code-sub { font-size:13px; color:#6B6356; }
.mr-code-card.dark .mr-code-sub { color:#A09888; }
.mr-share-link { font-size:11px; color:#8A827A; margin-top:10px; word-break:break-all; }

.mr-stat { background:#FFFFFF; border:1px solid #E8E2DA; border-radius:10px; padding:12px; text-align:center; cursor:pointer; transition:all .15s; position:relative; }
.mr-stat:hover { border-color:#BFA164; transform:translateY(-1px); }
.mr-stat.active { border-color:#BFA164; background:rgba(191,161,100,.10); box-shadow:0 2px 10px rgba(191,161,100,.18); }
.mr-stat.dark { background:#141414; border-color:#1E1E1E; }
.mr-stat.dark.active { background:rgba(191,161,100,.14); border-color:#BFA164; }
.mr-stat-val { font-size:22px; font-weight:800; color:#BFA164; font-family:'Courier New',monospace; }
.mr-stat-lbl { font-size:10px; color:#6B6356; text-transform:uppercase; letter-spacing:.5px; margin-top:2px; }

.mr-list-card { background:#FFFFFF; border:1px solid #E8E2DA; border-radius:12px; padding:18px; }
.mr-list-card.dark { background:#141414; border-color:#1E1E1E; }
.mr-list-row { display:flex; align-items:center; gap:12px; padding:10px 0; border-bottom:1px solid #F0EBE4; }
.mr-list-card.dark .mr-list-row { border-bottom-color:#1E1E1E; }
.mr-list-row:last-child { border-bottom:0; }
.mr-avatar { width:38px; height:38px; border-radius:50%; background:#BFA164; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:13px; flex-shrink:0; }

/* Clickable rows */
.mr-list-row-clickable { cursor:pointer; transition:background .15s; border-radius:8px; padding-left:8px; padding-right:8px; margin-left:-8px; margin-right:-8px; }
.mr-list-row-clickable:hover { background:rgba(191,161,100,.06); }
.mr-list-card.dark .mr-list-row-clickable:hover { background:rgba(191,161,100,.10); }

/* QR block on the code card */
.mr-qr-wrap { display:flex; flex-direction:column; align-items:center; margin-top:14px; }
.mr-qr-img { width:150px; height:150px; border-radius:10px; background:#FFFFFF; padding:8px; border:1px solid #E8E2DA; cursor:zoom-in; transition:transform .15s; }
.mr-qr-img:hover { transform:scale(1.02); }
.mr-code-card.dark .mr-qr-img { border-color:#2A2520; }
.mr-qr-hint { font-size:11px; color:#8A827A; margin-top:8px; }
.mr-code-card.dark .mr-qr-hint { color:#A09888; }

/* Modals */
.mr-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.55); display:flex; align-items:center; justify-content:center; z-index:9000; padding:16px; }
.mr-qr-modal, .mr-detail-modal { background:#FFFFFF; border-radius:14px; padding:24px; width:100%; max-width:420px; position:relative; text-align:center; }
.mr-detail-modal { text-align:left; max-width:480px; }
.mr-qr-modal.dark, .mr-detail-modal.dark { background:#141414; color:#F5F0E8; }
.mr-modal-close { position:absolute; top:8px; right:10px; background:transparent; border:none; font-size:22px; color:#6B6356; cursor:pointer; }
.mr-code-modal { font-size:32px; letter-spacing:6px; margin:4px 0 14px; }
.mr-qr-img-large { width:100%; max-width:360px; height:auto; border-radius:10px; background:#FFFFFF; padding:12px; border:1px solid #E8E2DA; }
.mr-qr-modal.dark .mr-qr-img-large { border-color:#2A2520; }

/* Detail tiles */
.mr-detail-tile { background:#F7F5F2; border-radius:10px; padding:12px; text-align:center; }
.mr-detail-tile.dark { background:#1A1510; }
.mr-detail-tile-val { font-size:22px; font-weight:800; color:#BFA164; font-family:'Courier New',monospace; }
.mr-detail-tile-lbl { font-size:10px; color:#6B6356; text-transform:uppercase; letter-spacing:.5px; margin-top:2px; }
.mr-detail-row { display:flex; justify-content:space-between; padding:8px 0; border-bottom:1px solid #F0EBE4; font-size:13px; color:#6B6356; }
.mr-detail-modal.dark .mr-detail-row { border-bottom-color:#1E1E1E; color:#A09888; }
.mr-detail-row strong { color:#1A1A1A; font-weight:600; }
.mr-detail-modal.dark .mr-detail-row strong { color:#F5F0E8; }
</style>
